import React, { useContext, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useParams } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { AuthContext } from '../contexts/AuthContext';
import { ScreenContext } from '../contexts/ScreenContext';
import { DataPulseContext } from '../contexts/DataPulseContext';
import ConfirmDialog from '../components/ConfirmDialog';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import IconButton from '@material-ui/core/IconButton';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import SendIcon from '@material-ui/icons/Send';
import ScheduleIcon from '@material-ui/icons/Schedule';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import CodeIcon from '@material-ui/icons/Code';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import GetAppIcon from '@material-ui/icons/GetApp';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import EventIcon from '@material-ui/icons/Event';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: theme.palette.grey[50],
    paddingBottom: theme.spacing(8), // Space for fixed action bar
  },
  header: {
    backgroundColor: 'white',
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 3),
    position: 'sticky',
    top: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5, 1),
      flexWrap: 'wrap',
      '& .MuiTypography-h4': {
        fontSize: '1.5rem',
      },
    },
  },
  content: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  splitContainer: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  formSection: {
    flex: 1,
    minWidth: 0, // Prevent flex item from overflowing
  },
  previewSection: {
    flex: 1,
    position: 'sticky',
    top: theme.spacing(10), // Below header
    maxHeight: `calc(100vh - ${theme.spacing(12)}px)`,
    overflowY: 'auto',
    [theme.breakpoints.down('md')]: {
      position: 'relative',
      top: 0,
      maxHeight: '600px',
      width: '100%',
    },
  },
  formPaper: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  previewPaper: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  previewContent: {
    backgroundColor: '#ffffff',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    minHeight: '400px',
    '& img': {
      maxWidth: '100%',
      height: 'auto',
    },
    // Support Quill alignment classes
    '& .ql-align-center': {
      textAlign: 'center',
    },
    '& .ql-align-right': {
      textAlign: 'right',
    },
    '& .ql-align-justify': {
      textAlign: 'justify',
    },
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  newsletterSection: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: 'relative',
    backgroundColor: theme.palette.grey[50],
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  sectionControls: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  actionBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'white',
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 3),
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    zIndex: 100,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 2),
      '& > button': {
        width: '100%',
      },
    },
  },
  recipientChip: {
    margin: theme.spacing(0.5),
  },
  imagePreview: {
    width: 60,
    height: 60,
    objectFit: 'cover',
    borderRadius: 4,
    marginRight: 10,
  },
  richTextEditor: {
    '& .quill': {
      backgroundColor: 'white',
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.divider}`,
    },
    '& .ql-toolbar': {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.grey[50],
    },
    '& .ql-container': {
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
      minHeight: 200,
      fontSize: 14,
    },
  },
  htmlModeToggle: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const CreateEmailCampaign = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams(); // Get campaign ID from URL for editing
  const { user, token } = useContext(AuthContext);
  const { setSnackbarMessage } = useContext(ScreenContext);
  const { surveys, loadSurveys } = useContext(DataPulseContext);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/';
  const isEditMode = !!id; // True if editing an existing draft

  // Email template HTML
  const EMAIL_HEADER = `
    <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 20px 0;">
      <tr>
        <td align="center">
          <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <tr>
              <td style="background-color: #ffffff; padding: 30px 20px; text-align: left; border-bottom: 1px solid #e5e7eb;">
                <img src="https://pcs-cdn.nyc3.cdn.digitaloceanspaces.com/ecs/assets/ecs-logo-c-f.png" alt="Personalized Computer Systems" style="max-width: 250px; height: auto;" />
              </td>
            </tr>
            <tr>
              <td style="padding: 40px 30px;">
  `;

  const EMAIL_FOOTER = (includeUnsubscribe) => `
              </td>
            </tr>
            <tr>
              <td style="background-color: #f9fafb; padding: 30px 20px; text-align: center; border-top: 1px solid #e5e7eb;">
                <p style="margin: 0 0 15px; color: #374151; font-size: 14px; font-weight: 600;">
                  Personalized Computer Systems, Inc.
                </p>
                <p style="margin: 0 0 10px; color: #6b7280; font-size: 13px;">
                  <a href="mailto:helpme@pcsinconline.com" style="color: #a11616; text-decoration: none;">helpme@pcsinconline.com</a> | 
                  <a href="tel:9372967416" style="color: #a11616; text-decoration: none;">(937) 296-7416</a>
                </p>
                <p style="margin: 0 0 15px; color: #6b7280; font-size: 12px;">
                  Open Hours: Mon – Fri: 8am – 5pm | Sat – Sun: CLOSED
                </p>
                <p style="margin: 0 0 5px; color: #6b7280; font-size: 12px;">
                  © ${new Date().getFullYear()} Personalized Computer Systems. All rights reserved.
                </p>
                ${includeUnsubscribe ? `
                <p style="margin: 15px 0 0; font-size: 11px;">
                  <a href="{{unsubscribe_url}}" style="color: #6b7280; text-decoration: underline;">Unsubscribe from this list</a>
                </p>
                ` : ''}
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `;

  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  const [templateType, setTemplateType] = useState('simple');
  const [includeUnsubscribe, setIncludeUnsubscribe] = useState(true);
  const [isHtmlMode, setIsHtmlMode] = useState(false); // Toggle for HTML source view
  const [newsletterHtmlMode, setNewsletterHtmlMode] = useState({}); // Track HTML mode per newsletter section
  
  // Quill editor refs for merge tag insertion
  const [simpleQuillRef, setSimpleQuillRef] = useState(null);
  const [alertQuillRef, setAlertQuillRef] = useState(null);
  const [newsletterQuillRefs, setNewsletterQuillRefs] = useState({});
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    from_email: '',
    from_name: '',
    reply_to: '',
    recipients: '', // Semicolon-separated emails
    html_body: '',
    campaign_type: 'email', // 'email' or 'survey'
    survey_id: null, // DataPulse survey ID when campaign_type='survey'
  });

  // State for test send
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Newsletter sections
  const [newsletterSections, setNewsletterSections] = useState([
    { id: 1, title: '', description: '', image_url: '', image_position: 'top' }
  ]);

  // Alert settings
  const [alertLevel, setAlertLevel] = useState('info');
  const [alertImage, setAlertImage] = useState('');

  // Simple/Alert image
  const [simpleImage, setSimpleImage] = useState('');
  const [simpleImagePosition, setSimpleImagePosition] = useState('top');

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  // Scheduling state
  const [scheduledSendTime, setScheduledSendTime] = useState('');
  const [enableScheduling, setEnableScheduling] = useState(false);
  // Auto-detect user's timezone from browser, fallback to Eastern
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  const [selectedTimezone, setSelectedTimezone] = useState(browserTimezone);

  // CTA settings
  const [ctaEnabled, setCtaEnabled] = useState(false);
  const [ctaMessage, setCtaMessage] = useState('');
  const [ctaButtonText, setCtaButtonText] = useState('');
  const [ctaButtonUrl, setCtaButtonUrl] = useState('');

  // Recipient lists
  const [recipientLists, setRecipientLists] = useState([]);
  const [selectedListName, setSelectedListName] = useState('');

  // Uploaded images tracking
  const [uploadedImages, setUploadedImages] = useState([]);

  // Auto-populate survey body when survey is selected
  useEffect(() => {
    if (formData.campaign_type === 'survey' && formData.survey_id && !formData.html_body) {
      const selectedSurvey = surveys.find(s => s.id === formData.survey_id);
      if (selectedSurvey) {
        const defaultBody = `<div style="text-align: center; padding: 20px 0;">
  <h2 style="color: #a11616; margin: 0 0 20px; font-size: 28px; font-weight: 600;">
    We'd Love Your Feedback!
  </h2>
</div>

<div style="color: #374151; font-size: 16px; line-height: 1.6; margin-bottom: 30px;">
  <p style="margin: 0 0 15px;">Hello {{first_name}},</p>
  
  <p style="margin: 0 0 15px;">
    We're conducting a survey and would greatly appreciate your input. 
    Your feedback helps us improve our services and better meet your needs.
  </p>
  
  <p style="margin: 0 0 20px;">
    Survey: <strong>${selectedSurvey.name}</strong>
  </p>
  ${selectedSurvey.description ? `
  <p style="margin: 0 0 20px; font-style: italic; color: #6b7280;">
    ${selectedSurvey.description}
  </p>` : ''}
</div>`;
        setFormData(prev => ({ ...prev, html_body: defaultBody }));
        
        // Auto-enable CTA for survey campaigns with pre-filled message
        setCtaEnabled(true);
        setCtaButtonText('Take Survey');
        setCtaButtonUrl('{{survey_link}}');
        setCtaMessage('Your feedback is important to us. Click the button below to complete the survey.');
      }
    }
  }, [formData.campaign_type, formData.survey_id, surveys]);

  // Clear body when switching from survey to email campaign
  useEffect(() => {
    // Only clear if body contains survey_link (indicates it's survey content)
    if (formData.campaign_type === 'email' && formData.html_body.includes('{{survey_link}}')) {
      setFormData(prev => ({ ...prev, html_body: '' }));
    }
  }, [formData.campaign_type]);

  // Load draft data if editing
  useEffect(() => {
    if (isEditMode && id) {
      const loadDraft = async () => {
        try {
          const response = await axios.get(`${API_URL}email-campaigns/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          
          const campaign = response.data.campaign;
          
          if (campaign.status !== 'draft') {
            setSnackbarMessage({ 
              message: 'Only draft campaigns can be edited', 
              severity: 'error' 
            });
            history.push('/email-campaigns');
            return;
          }

          // Load the campaign data into the form
          let bodyContent = campaign.body || '';
          let ctaData = null;
          
          // Extract CTA data if present
          const ctaMatch = bodyContent.match(/<!-- CTA_DATA:(.*?) -->\n?/);
          if (ctaMatch) {
            try {
              ctaData = JSON.parse(ctaMatch[1]);
              // Remove the CTA metadata comment from the body
              bodyContent = bodyContent.replace(/<!-- CTA_DATA:.*? -->\n?/, '');
            } catch (e) {
              console.error('Error parsing CTA data:', e);
            }
          }

          setFormData({
            name: campaign.name || '',
            subject: campaign.subject || '',
            from_email: campaign.from_email || '',
            from_name: campaign.from_name || '',
            reply_to: campaign.reply_to || '',
            recipients: campaign.recipients || '',
            html_body: bodyContent,
          });
          
          // Load CTA data if it exists
          if (ctaData && ctaData.enabled) {
            setCtaEnabled(true);
            setCtaMessage(ctaData.message || '');
            setCtaButtonText(ctaData.buttonText || '');
            setCtaButtonUrl(ctaData.buttonUrl || '');
          }
          
          setSnackbarMessage({ 
            message: `Editing draft: ${campaign.name}`, 
            severity: 'info' 
          });
        } catch (error) {
          console.error('Error loading draft:', error);
          setSnackbarMessage({ 
            message: 'Failed to load draft campaign', 
            severity: 'error' 
          });
          history.push('/email-campaigns');
        }
      };

      loadDraft();
    }
  }, [isEditMode, id, token, API_URL, history, setSnackbarMessage]);

  // Load recipient lists
  useEffect(() => {
    axios.get(`${API_URL}email-campaigns/lists`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => {
      setRecipientLists(response.data);
    })
    .catch(error => {
      console.error('Error loading recipient lists:', error);
    });
  }, [API_URL, token]);

  // Load DataPulse surveys when campaign type is survey
  useEffect(() => {
    if (formData.campaign_type === 'survey' && !surveys.length) {
      loadSurveys();
    }
  }, [formData.campaign_type, surveys, loadSurveys]);

  const fromEmailOptions = [
    { 
      value: 'user', 
      label: 'On My Behalf', 
      email: user?.email || '', 
      name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() 
    },
    { value: 'noreply', label: 'Do Not Reply', email: 'donotreply@pcsinconline.com', name: 'PCS Inc Online' },
    { value: 'campaigns', label: 'Campaigns', email: 'campaigns@pcsinconline.com', name: 'PCS Inc Online Campaigns' },
    { value: 'marketing', label: 'Marketing', email: 'marketing@pcsinconline.com', name: 'PCS Inc Online Marketing' },
    { value: 'alerts', label: 'Alerts', email: 'alerts@pcsinconline.com', name: 'PCS Inc Online Alerts' },
    { value: 'info', label: 'Info', email: 'info@pcsinconline.com', name: 'PCS Inc Online Info' },
    { value: 'newsletter', label: 'Newsletter', email: 'newsletter@pcsinconline.com', name: 'PCS Inc Online Newsletter' },
  ];

  const templateTypeOptions = [
    { value: 'simple', label: 'Simple Message', description: 'Basic email with text content' },
    { value: 'newsletter', label: 'Newsletter', description: 'Multi-section email with articles' },
    { value: 'alert', label: 'Alert/System Message', description: 'Important notifications with priority flags' },
    { value: 'blank', label: 'Blank Template', description: 'No header or footer, maximum flexibility' },
  ];

  const alertLevelOptions = [
    { value: 'info', label: 'Info', color: '#2196F3', icon: 'ℹ️' },
    { value: 'warning', label: 'Warning', color: '#FF9800', icon: '⚠️' },
    { value: 'critical', label: 'Critical', color: '#F44336', icon: '🚨' },
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFromEmailChange = (value) => {
    const selected = fromEmailOptions.find(opt => opt.value === value);
    if (selected) {
      setFormData(prev => ({
        ...prev,
        from_email: selected.email,
        from_name: selected.name,
      }));
    }
  };

  // Handle recipient list selection
  const handleRecipientListSelect = (event) => {
    const listId = event.target.value;
    
    if (!listId) {
      // User selected "None - enter manually"
      setSelectedListName('');
      return;
    }

    // Load recipients from the selected list
    axios.get(`${API_URL}email-campaigns/lists/${listId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => {
      const list = response.data;
      const recipients = list.recipients || [];
      
      // Format recipients as CSV with headers to preserve first_name and last_name
      // This format is parsed correctly by the backend's parseRecipients function
      const csvHeader = 'email,first_name,last_name';
      const csvRows = recipients.map(r => {
        const email = r.email || '';
        const firstName = r.first_name || '';
        const lastName = r.last_name || '';
        return `${email},${firstName},${lastName}`;
      });
      
      const recipientsCsv = [csvHeader, ...csvRows].join('\n');
      
      handleInputChange('recipients', recipientsCsv);
      setSelectedListName(list.name);
      
      // Count how many have names
      const withNames = recipients.filter(r => r.first_name || r.last_name).length;
      const message = withNames > 0 
        ? `Loaded ${recipients.length} recipient(s) from "${list.name}" (${withNames} with names)`
        : `Loaded ${recipients.length} recipient(s) from "${list.name}"`;
      
      setSnackbarMessage({ 
        message: message, 
        severity: 'success' 
      });
    })
    .catch(error => {
      console.error('Error loading list recipients:', error);
      setSnackbarMessage({ 
        message: 'Failed to load recipients from list', 
        severity: 'error' 
      });
    });
  };

  // Newsletter section handlers
  const handleAddSection = () => {
    const newId = Math.max(...newsletterSections.map(s => s.id), 0) + 1;
    setNewsletterSections([...newsletterSections, {
      id: newId,
      title: '',
      description: '',
      image_url: '',
      image_position: 'top'
    }]);
  };

  const handleRemoveSection = (id) => {
    if (newsletterSections.length > 1) {
      setNewsletterSections(newsletterSections.filter(s => s.id !== id));
    }
  };

  const handleMoveSectionUp = (index) => {
    if (index > 0) {
      const newSections = [...newsletterSections];
      [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
      setNewsletterSections(newSections);
    }
  };

  const handleMoveSectionDown = (index) => {
    if (index < newsletterSections.length - 1) {
      const newSections = [...newsletterSections];
      [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
      setNewsletterSections(newSections);
    }
  };

  const handleSectionChange = (id, field, value) => {
    setNewsletterSections(newsletterSections.map(section =>
      section.id === id ? { ...section, [field]: value } : section
    ));
  };

  // Image upload handler
  const handleImageUpload = async (event, callback) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setSnackbarMessage({ message: 'Please select an image file', severity: 'error' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setSnackbarMessage({ message: 'Image size must be less than 5MB', severity: 'error' });
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await axios.post(`${API_URL}email-campaigns/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.imageUrl) {
        const imageData = {
          url: response.data.imageUrl,
          name: file.name,
          uploadedAt: new Date()
        };
        setUploadedImages([...uploadedImages, imageData]);
        
        if (callback) {
          callback(response.data.imageUrl);
        }
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setSnackbarMessage({ message: 'Failed to upload image. Please try again.', severity: 'error' });
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbarMessage({ message: 'URL copied to clipboard!', severity: 'success' });
  };

  // CSV Template Download
  const handleDownloadTemplate = () => {
    const csvContent = 'email,first_name,last_name,username\njohn@example.com,John,Doe,johndoe\njane@example.com,Jane,Smith,janesmith\nbob@example.com,Bob,Johnson,';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'recipient-list-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // CSV File Upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      // Populate the textarea with the uploaded content
      handleInputChange('recipients', content);
    };
    reader.readAsText(file);
    
    // Reset input so same file can be uploaded again
    event.target.value = '';
  };

  // Insert merge tag into editor
  const insertMergeTag = (tag, quillRef) => {
    if (!quillRef) {
      // Fallback for HTML mode or if ref not available
      const mergeTag = `{{${tag}}}`;
      if (isHtmlMode) {
        const currentContent = formData.html_body || '';
        handleInputChange('html_body', currentContent + mergeTag);
      }
      return;
    }

    const editor = quillRef.getEditor();
    const range = editor.getSelection(true);
    const position = range ? range.index : editor.getLength();
    editor.insertText(position, `{{${tag}}}`);
    editor.setSelection(position + tag.length + 4); // Move cursor after the tag
  };

  // Merge tag component
  const MergeTagButtons = ({ quillRef, editorId, campaignType }) => {
    const mergeTags = [
      { label: 'First Name', tag: 'first_name' },
      { label: 'Last Name', tag: 'last_name' },
      { label: 'Full Name', tag: 'full_name' },
      { label: 'Email', tag: 'email' },
      { label: 'Username', tag: 'username' },
    ];

    // Add survey_link button only for survey campaigns
    if (campaignType === 'survey') {
      mergeTags.push({ label: 'Survey Link', tag: 'survey_link', color: 'primary' });
    }

    return (
      <Box mb={1.5} display="flex" flexWrap="wrap" style={{ gap: '5px' }} alignItems="center">
        <Typography variant="caption" color="textSecondary" style={{ marginRight: 8 }}>
          Insert:
        </Typography>
        {mergeTags.map((item) => (
          <Button
            key={item.tag}
            size="small"
            variant="outlined"
            color={item.color || 'default'}
            onClick={() => insertMergeTag(item.tag, quillRef)}
            style={{ 
              textTransform: 'none',
              borderRadius: 4,
              padding: '4px 12px',
              fontSize: '0.75rem',
              minWidth: 'auto'
            }}
          >
            {item.label}
          </Button>
        ))}
      </Box>
    );
  };

  // Parse recipient emails
  const getRecipientCount = () => {
    if (!formData.recipients) return 0;
    
    const recipientsText = formData.recipients.trim();
    if (!recipientsText) return 0;
    
    const lines = recipientsText.split('\n').filter(line => line.trim());
    
    // Check if it's CSV format (has commas or tabs)
    const hasCommaOrTab = recipientsText.includes(',') || recipientsText.includes('\t');
    
    if (hasCommaOrTab) {
      // CSV format - count lines with @ symbols (excluding potential headers)
      return lines.filter(line => {
        const values = line.split(/[,\t]/);
        return values.some(v => v && v.includes('@'));
      }).length;
    } else {
      // Semicolon-separated format
      return recipientsText
        .split(';')
        .map(email => email.trim())
        .filter(email => email.length > 0)
        .length;
    }
  };

  // Validate email format
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Form validation
  const validateForm = () => {
    const errors = [];

    if (!formData.name.trim()) errors.push('Campaign name is required');
    if (!formData.subject.trim()) errors.push('Subject line is required');
    if (!formData.from_email) errors.push('From email is required');
    if (!formData.from_name.trim()) errors.push('From name is required');

    // Survey campaign specific validation
    if (formData.campaign_type === 'survey') {
      if (!formData.survey_id) {
        errors.push('Please select a survey for this campaign');
      }
    }

    if (formData.reply_to && !validateEmail(formData.reply_to)) {
      errors.push('Reply to email is invalid');
    }

    // Validate recipients
    if (!formData.recipients.trim()) {
      errors.push('At least one recipient is required');
    } else {
      // Parse recipients - support both semicolon-separated and CSV format
      const recipientsText = formData.recipients.trim();
      const lines = recipientsText.split('\n').filter(line => line.trim());
      
      let emails = [];
      
      // Check if it's CSV format (has commas or tabs)
      const hasCommaOrTab = recipientsText.includes(',') || recipientsText.includes('\t');
      
      if (hasCommaOrTab) {
        // CSV format - extract emails from each line
        lines.forEach(line => {
          const values = line.split(/[,\t]/).map(v => v.trim());
          // Find the email (contains @)
          const email = values.find(v => v && v.includes('@'));
          if (email) {
            emails.push(email);
          }
        });
      } else {
        // Semicolon-separated format
        emails = recipientsText.split(';').map(e => e.trim()).filter(e => e);
      }
      
      if (emails.length === 0) {
        errors.push('No valid email addresses found in recipients');
      } else {
        const invalidEmails = emails.filter(email => !validateEmail(email));
        if (invalidEmails.length > 0) {
          errors.push(`Invalid email format: ${invalidEmails.join(', ')}`);
        }
      }
    }

    // Template-specific validations
    if (templateType === 'newsletter') {
      const hasContent = newsletterSections.some(s => s.title.trim() || s.description.trim());
      if (!hasContent) {
        errors.push('At least one newsletter section must have a title or description');
      }
    } else if (formData.campaign_type === 'email' && (templateType === 'simple' || templateType === 'alert') && !formData.html_body.trim()) {
      errors.push('Email content is required');
    } else if (formData.campaign_type === 'survey' && !formData.html_body.trim()) {
      errors.push('Survey email content is required');
    }

    if (errors.length > 0) {
      setSnackbarMessage({ message: 'Please fix the following errors:\n\n' + errors.join('\n'), severity: 'error' });
      return false;
    }

    return true;
  };

  // Build complete email HTML
  const buildEmailHTML = () => {
    // Handle survey campaigns - use user's custom body
    if (formData.campaign_type === 'survey') {
      if (!formData.html_body) return '';
      let surveyContent = formData.html_body;
      // Add CTA button for survey campaigns
      if (ctaEnabled && ctaButtonText && ctaButtonUrl) {
        surveyContent += buildCTAHTML();
      }
      return EMAIL_HEADER + surveyContent + EMAIL_FOOTER(includeUnsubscribe);
    }

    if (templateType === 'blank') {
      let blankContent = formData.html_body;
      if (ctaEnabled && ctaButtonText && ctaButtonUrl) {
        blankContent += buildCTAHTML();
      }
      return blankContent;
    }

    let content = '';

    if (templateType === 'simple') {
      if (simpleImage && simpleImagePosition === 'top') {
        content += `<img src="${simpleImage}" alt="Content Image" style="max-width: 100%; height: auto; margin-bottom: 20px; border-radius: 4px;" />`;
      }
      
      content += `<div style="color: #374151; font-size: 16px; line-height: 1.6;">${formData.html_body}</div>`;
      
      if (simpleImage && simpleImagePosition === 'bottom') {
        content += `<img src="${simpleImage}" alt="Content Image" style="max-width: 100%; height: auto; margin-top: 20px; border-radius: 4px;" />`;
      }
    } else if (templateType === 'newsletter') {
      content = newsletterSections.map(section => {
        let sectionHTML = '<div style="margin-bottom: 30px;">';
        
        if (section.image_url && section.image_position === 'top') {
          sectionHTML += `<img src="${section.image_url}" alt="${section.title}" style="max-width: 100%; height: auto; margin-bottom: 15px; border-radius: 4px;" />`;
        }
        
        if (section.title) {
          sectionHTML += `<h2 style="color: #a11616; margin: 0 0 10px; font-size: 18px; font-weight: 600;">${section.title}</h2>`;
        }
        
        if (section.description) {
          sectionHTML += `<p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 15px;">${section.description}</p>`;
        }
        
        if (section.image_url && section.image_position === 'bottom') {
          sectionHTML += `<img src="${section.image_url}" alt="${section.title}" style="max-width: 100%; height: auto; margin-top: 15px; border-radius: 4px;" />`;
        }
        
        sectionHTML += '</div>';
        return sectionHTML;
      }).join('');
    } else if (templateType === 'alert') {
      const alertColors = {
        info: { bg: '#dbeafe', border: '#3b82f6', text: '#1e3a8a' },
        warning: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
        critical: { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' }
      };
      const colors = alertColors[alertLevel];
      const alertIcon = alertLevelOptions.find(opt => opt.value === alertLevel)?.icon || '';
      
      // Alert box - ONLY contains icon and alert level text
      content += `
        <div style="background-color: ${colors.bg}; border-left: 4px solid ${colors.border}; padding: 20px; border-radius: 4px; margin-bottom: 20px;">
          <div style="display: flex; align-items: flex-start; gap: 15px;">
            <div style="font-size: 28px; line-height: 28px; flex-shrink: 0;">${alertIcon}</div>
            <div style="flex: 1; min-width: 0;">
              <h3 style="color: ${colors.text}; margin: 0; padding: 0; font-size: 20px; text-transform: uppercase; line-height: 28px;">
                ${alertLevel.charAt(0).toUpperCase() + alertLevel.slice(1)} Alert
              </h3>
            </div>
          </div>
        </div>
      `;
      
      // Image (optional) - positioned above or below message content
      if (alertImage && simpleImagePosition === 'top') {
        content += `<img src="${alertImage}" alt="Alert" style="max-width: 100%; height: auto; margin-bottom: 20px; border-radius: 4px;" />`;
      }
      
      // User's message content - OUTSIDE the alert box
      content += `
        <div style="color: #374151; font-size: 16px; line-height: 1.6; margin-bottom: 20px;">
          ${formData.html_body}
        </div>
      `;
      
      // Image at bottom position (if selected)
      if (alertImage && simpleImagePosition === 'bottom') {
        content += `<img src="${alertImage}" alt="Alert" style="max-width: 100%; height: auto; margin-top: 20px; border-radius: 4px;" />`;
      }
    }

    if (ctaEnabled && ctaButtonText && ctaButtonUrl) {
      content += buildCTAHTML();
    }

    return EMAIL_HEADER + content + EMAIL_FOOTER(includeUnsubscribe);
  };

  // Build CTA HTML
  const buildCTAHTML = () => {
    // Don't add tracking params to survey_link placeholder
    const trackedUrl = ctaButtonUrl === '{{survey_link}}' 
      ? ctaButtonUrl 
      : `${ctaButtonUrl}${ctaButtonUrl.includes('?') ? '&' : '?'}utm_source=email_campaign&utm_medium=email`;
    
    return `
      <div style="margin-top: 30px; text-align: center;">
        ${ctaMessage ? `<p style="color: #374151; font-size: 16px; margin-bottom: 20px; line-height: 1.6;">${ctaMessage}</p>` : ''}
        <a href="${trackedUrl}" style="display: inline-block; background-color: #a11616; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 6px; font-size: 16px; font-weight: 600; box-shadow: 0 2px 4px rgba(161, 22, 22, 0.3);">
          ${ctaButtonText}
        </a>
      </div>
    `;
  };

  // Save as draft
  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      // Store CTA data as JSON comment in the HTML so we can extract it when loading
      let bodyWithMetadata = formData.html_body;
      
      if (ctaEnabled && (ctaMessage || ctaButtonText || ctaButtonUrl)) {
        const ctaData = {
          enabled: true,
          message: ctaMessage,
          buttonText: ctaButtonText,
          buttonUrl: ctaButtonUrl
        };
        bodyWithMetadata = `<!-- CTA_DATA:${JSON.stringify(ctaData)} -->\n${formData.html_body}`;
      }

      const payload = {
        name: formData.name,
        subject: formData.subject,
        body: bodyWithMetadata, // Save raw body with CTA metadata, NOT the full HTML template
        recipients: formData.recipients,
        fromEmail: formData.from_email,
        fromName: formData.from_name,
        replyTo: formData.reply_to || null,
        status: 'draft',
        campaign_type: formData.campaign_type,
        survey_id: formData.survey_id || null,
      };

      if (isEditMode && id) {
        // UPDATE existing draft
        await axios.put(`${API_URL}email-campaigns/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSnackbarMessage({ message: 'Campaign draft updated successfully!', severity: 'success' });
      } else {
        // CREATE new draft
        await axios.post(`${API_URL}email-campaigns/save-draft`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSnackbarMessage({ message: 'Campaign saved as draft successfully!', severity: 'success' });
      }

      history.push('/email-campaigns');
    } catch (error) {
      console.error('Error saving draft:', error);
      setSnackbarMessage({ message: 'Failed to save draft. Please try again.', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  // Send campaign
  const handleSendNow = async () => {
    if (!validateForm()) return;

    const recipientCount = getRecipientCount();
    
    // Check if scheduling is enabled
    if (enableScheduling && scheduledSendTime) {
      // User entered time in their selected timezone, convert to UTC
      // The datetime-local input gives us a string like "2025-11-07T14:30"
      // We need to interpret this as being in the selected timezone, then convert to UTC
      
      try {
        const [datePart, timePart] = scheduledSendTime.split('T');
        const [year, month, day] = datePart.split('-');
        const [hour, minute] = timePart.split(':');
        
        // IMPORTANT: datetime-local input returns time in browser's local timezone
        // We need to convert it to the SELECTED timezone, then to UTC
        
        // Create a date string that will be interpreted in the selected timezone
        // We'll create the date in UTC, then calculate what UTC time corresponds to 
        // the selected timezone's local time
        
        // Method: Create date parts as if they were UTC, get that date's time in selected TZ,
        // calculate difference, adjust
        const dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:00`;
        
        // Parse as browser local time first
        const localDate = new Date(dateStr);
        
        // Get what time this appears as in the selected timezone
        const selectedTzTime = localDate.toLocaleString('en-US', {
          timeZone: selectedTimezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
        
        // Get what time this appears as in UTC
        const utcTime = localDate.toLocaleString('en-US', {
          timeZone: 'UTC',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
        
        // Calculate the offset between what the user entered and what it shows in selected TZ
        // Parse both back to dates to get the difference
        const [selDate, selTime] = selectedTzTime.split(', ');
        const [selM, selD, selY] = selDate.split('/');
        const [selH, selMin, selS] = selTime.split(':');
        const selectedTzDate = new Date(Date.UTC(selY, selM - 1, selD, selH, selMin, selS));
        
        // The user wanted: year-month-day hour:minute in SELECTED timezone
        // Build what they wanted as UTC
        const wantedUtcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
        
        // Calculate offset: how many ms difference between what we have and what we want
        const offset = wantedUtcDate.getTime() - selectedTzDate.getTime();
        
        // Apply offset to get the correct UTC time
        const correctUtcDate = new Date(localDate.getTime() + offset);
        
        // Validation
        const now = new Date();
        if (correctUtcDate <= now) {
          setSnackbarMessage({ 
            message: `Scheduled send time must be in the future. Current time in ${selectedTimezone}: ${now.toLocaleString('en-US', { timeZone: selectedTimezone })}`, 
            severity: 'error' 
          });
          return;
        }
        
        // Show confirmation with timezone info
        const localTimeStr = correctUtcDate.toLocaleString('en-US', { 
          timeZone: selectedTimezone,
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        const utcTimeStr = correctUtcDate.toLocaleString('en-US', { 
          timeZone: 'UTC',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZoneName: 'short'
        });
        
        setConfirmDialog({
          open: true,
          title: 'Schedule Campaign',
          message: `Schedule this campaign to be sent to ${recipientCount} recipient(s)?\n\nSend Time in ${selectedTimezone}:\n${localTimeStr}\n\nServer Time (UTC):\n${utcTimeStr}\n\nThe campaign will be sent at the ${selectedTimezone} time shown above.`,
          onConfirm: () => executeSend(correctUtcDate.toISOString()),
        });
      } catch (error) {
        console.error('Error parsing scheduled time:', error);
        setSnackbarMessage({ message: 'Invalid scheduled time format', severity: 'error' });
        return;
      }
    } else {
      // Show confirm dialog for immediate send
      setConfirmDialog({
        open: true,
        title: 'Confirm Send Campaign',
        message: `Are you sure you want to send this campaign to ${recipientCount} recipient(s)?\n\nThis action cannot be undone.`,
        onConfirm: () => executeSend(null),
      });
    }
  };

  const executeSend = async (scheduledTime = null) => {
    setConfirmDialog({ ...confirmDialog, open: false });
    setIsSending(true);
    
    try {
      // If editing a draft, delete it first so /send can create the final campaign
      if (isEditMode && id) {
        await axios.delete(`${API_URL}email-campaigns/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      // Now create the campaign (sent or scheduled)
      const payload = {
        name: formData.name,
        subject: formData.subject,
        body: buildEmailHTML(),
        recipients: formData.recipients,
        fromEmail: formData.from_email,
        fromName: formData.from_name,
        replyTo: formData.reply_to || null,
        campaign_type: formData.campaign_type,
        survey_id: formData.survey_id || null,
      };

      // Add scheduled time if provided
      if (scheduledTime) {
        payload.scheduledSendTime = scheduledTime;
      }

      const response = await axios.post(`${API_URL}email-campaigns/send`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { recipientCount: sent, skippedCount, skippedEmails, scheduledSendTime: scheduled } = response.data;
      
      let message;
      if (scheduled) {
        message = `Campaign scheduled successfully for ${new Date(scheduled).toLocaleString()}!\n\n${sent} recipient(s) will receive the email at that time.`;
      } else {
        message = `Campaign sent successfully to ${sent} recipient(s)!`;
      }
      
      if (skippedCount > 0) {
        message += `\n\n${skippedCount} recipient(s) were skipped (unsubscribed):\n${skippedEmails.join(', ')}`;
      }
      
      setSnackbarMessage({ message, severity: 'success' });
      history.push('/email-campaigns');
    } catch (error) {
      console.error('Error sending campaign:', error);
      setSnackbarMessage({ message: error.response?.data?.error || 'Failed to send campaign. Please try again.', severity: 'error' });
    } finally {
      setIsSending(false);
    }
  };

  // Handle test send to current user's email
  const handleTestSend = async () => {
    // Basic validation
    if (!formData.subject.trim()) {
      setSnackbarMessage({ message: 'Subject line is required for test send', severity: 'error' });
      return;
    }
    if (!formData.from_email) {
      setSnackbarMessage({ message: 'From email is required for test send', severity: 'error' });
      return;
    }
    if (!formData.from_name.trim()) {
      setSnackbarMessage({ message: 'From name is required for test send', severity: 'error' });
      return;
    }

    // Survey-specific validation
    if (formData.campaign_type === 'survey') {
      if (!formData.survey_id) {
        setSnackbarMessage({ message: 'Please select a survey for test send', severity: 'error' });
        return;
      }
    } else if (formData.campaign_type === 'email' && !formData.html_body.trim()) {
      setSnackbarMessage({ message: 'Email content is required for test send', severity: 'error' });
      return;
    }

    setIsSendingTest(true);

    try {
      const emailBody = buildEmailHTML();
      
      const payload = {
        subject: formData.subject,
        body: emailBody,
        fromEmail: formData.from_email,
        fromName: formData.from_name,
        campaign_type: formData.campaign_type,
        survey_id: formData.survey_id || null,
      };

      await axios.post(`${API_URL}email-campaigns/test-send`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSnackbarMessage({ 
        message: `Test email sent to ${user.email}! Check your inbox.`, 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Test send error:', error);
      setSnackbarMessage({ 
        message: error.response?.data?.error || 'Failed to send test email', 
        severity: 'error' 
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <IconButton onClick={() => history.push('/email-campaigns')} edge="start">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">
          {isEditMode ? 'Edit Campaign Draft' : 'Create Email Campaign'}
        </Typography>
      </div>

      {/* Content */}
      <div className={classes.content}>
        <Grid container className={classes.splitContainer}>
          {/* Left: Form */}
          <Grid item xs={12} md={6} className={classes.formSection}>
            <Paper className={classes.formPaper}>
              <Typography variant="h6" gutterBottom>
                Campaign Details
              </Typography>

              <TextField
                label="Campaign Name"
                fullWidth
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={classes.formField}
                variant="outlined"
                placeholder="Internal name for this campaign"
              />

              <Box className={classes.formField}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
                  Campaign Type
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={formData.campaign_type}
                    onChange={(e) => handleInputChange('campaign_type', e.target.value)}
                  >
                    <FormControlLabel 
                      value="email" 
                      control={<Radio color="primary" />} 
                      label="Regular Email Campaign" 
                    />
                    <FormControlLabel 
                      value="survey" 
                      control={<Radio color="primary" />} 
                      label="Survey Campaign" 
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {formData.campaign_type === 'survey' && (
                <FormControl fullWidth variant="outlined" className={classes.formField}>
                  <InputLabel>Select Survey</InputLabel>
                  <Select
                    value={formData.survey_id || ''}
                    onChange={(e) => handleInputChange('survey_id', e.target.value)}
                    label="Select Survey"
                  >
                    <MenuItem value="">
                      <em>Select a survey</em>
                    </MenuItem>
                    {surveys
                      .filter(s => s.status === 'active')
                      .map((survey) => {
                        const questionCount = survey.question_count || 0;
                        // Don't show surveys with no questions
                        if (questionCount === 0) return null;
                        return (
                          <MenuItem key={survey.id} value={survey.id}>
                            {survey.name} ({questionCount} question{questionCount !== 1 ? 's' : ''})
                          </MenuItem>
                        );
                      })
                      .filter(Boolean)}
                  </Select>
                </FormControl>
              )}

              {formData.campaign_type === 'email' && (
                <FormControl fullWidth variant="outlined" className={classes.formField}>
                  <InputLabel>Template Type</InputLabel>
                  <Select
                    value={templateType}
                    onChange={(e) => setTemplateType(e.target.value)}
                    label="Template Type"
                  >
                    {templateTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <div>
                          <Typography variant="body1">{option.label}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {option.description}
                          </Typography>
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {formData.campaign_type === 'survey' && formData.survey_id && (
                <Box className={classes.formField} style={{ padding: '12px 16px', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: 4 }}>
                  <Typography variant="body2" style={{ color: '#1565c0', fontWeight: 500, marginBottom: 4 }}>
                    ✓ Survey Selected - Template Auto-Generated
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    A default email template with survey button has been created. Customize the message and button text below. The survey link is automatically generated for each recipient.
                  </Typography>
                </Box>
              )}

              <TextField
                label="Subject Line"
                fullWidth
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className={classes.formField}
                variant="outlined"
                placeholder="What recipients will see in their inbox"
              />

              <FormControl fullWidth variant="outlined" className={classes.formField}>
                <InputLabel>From Email</InputLabel>
                <Select
                  value={fromEmailOptions.find(opt => opt.email === formData.from_email)?.value || ''}
                  onChange={(e) => handleFromEmailChange(e.target.value)}
                  label="From Email"
                >
                  {fromEmailOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label} ({option.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="From Name"
                fullWidth
                value={formData.from_name}
                onChange={(e) => handleInputChange('from_name', e.target.value)}
                className={classes.formField}
                variant="outlined"
                helperText="Name that appears in recipient's inbox"
              />

              <TextField
                label="Reply To Email (Optional)"
                fullWidth
                value={formData.reply_to}
                onChange={(e) => handleInputChange('reply_to', e.target.value)}
                className={classes.formField}
                variant="outlined"
                placeholder="Where replies will be sent"
              />

              <Divider style={{ margin: '24px 0' }} />

              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Recipients
                </Typography>
                <Box display="flex" gap={1}>
                  <input
                    accept=".csv,.txt"
                    style={{ display: 'none' }}
                    id="csv-upload-button"
                    type="file"
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="csv-upload-button">
                    <Button
                      variant="outlined"
                      component="span"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload CSV
                    </Button>
                  </label>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<GetAppIcon />}
                    onClick={handleDownloadTemplate}
                  >
                    Download Template
                  </Button>
                </Box>
              </Box>

              <FormControl fullWidth variant="outlined" className={classes.formField}>
                <InputLabel id="recipient-list-label">Load from Recipient List (Optional)</InputLabel>
                <Select
                  labelId="recipient-list-label"
                  value=""
                  onChange={handleRecipientListSelect}
                  label="Load from Recipient List (Optional)"
                >
                  <MenuItem value="" disabled>
                    <em>Select a recipient list...</em>
                  </MenuItem>
                  {recipientLists.map((list) => (
                    <MenuItem key={list.id} value={list.id}>
                      {list.name} ({list.recipient_count || 0} recipient{list.recipient_count !== 1 ? 's' : ''})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Email Addresses"
                fullWidth
                multiline
                rows={4}
                value={formData.recipients}
                onChange={(e) => handleInputChange('recipients', e.target.value)}
                className={classes.formField}
                variant="outlined"
                placeholder="Enter email addresses separated by semicolons (;)&#10;e.g., john@example.com; jane@example.com&#10;&#10;Or paste CSV format:&#10;email,first_name,last_name&#10;john@example.com,John,Doe&#10;jane@example.com,Jane,Smith"
                helperText={selectedListName ? `Loaded from "${selectedListName}" - ${getRecipientCount()} recipient(s)` : `${getRecipientCount()} recipient(s)`}
              />

              <Divider style={{ margin: '24px 0' }} />

              {/* Content Editor - show for regular emails and survey campaigns */}
              {(formData.campaign_type === 'email' || formData.campaign_type === 'survey') && (
                <>
                  <Typography variant="h6" gutterBottom>
                    {formData.campaign_type === 'survey' ? 'Survey Email Content' : 'Content'}
                  </Typography>

                  {/* Template-specific content */}
                  {(templateType === 'simple' || formData.campaign_type === 'survey') && (
                <Box>
                  <Box className={classes.htmlModeToggle}>
                    <Button
                      size="small"
                      startIcon={<CodeIcon />}
                      onClick={() => setIsHtmlMode(!isHtmlMode)}
                      variant={isHtmlMode ? 'contained' : 'outlined'}
                      color={isHtmlMode ? 'primary' : 'default'}
                    >
                      {isHtmlMode ? 'Visual Editor' : 'HTML Source'}
                    </Button>
                  </Box>

                  {isHtmlMode ? (
                    <TextField
                      label="HTML Source"
                      fullWidth
                      multiline
                      rows={12}
                      value={formData.html_body}
                      onChange={(e) => handleInputChange('html_body', e.target.value)}
                      className={classes.formField}
                      variant="outlined"
                      placeholder="<p>Enter HTML here...</p>"
                      InputProps={{
                        style: { fontFamily: 'monospace', fontSize: 13 }
                      }}
                    />
                  ) : (
                    <>
                      <MergeTagButtons quillRef={simpleQuillRef} editorId="simple" campaignType={formData.campaign_type} />
                      <Box className={classes.richTextEditor}>
                        <ReactQuill
                          ref={(el) => setSimpleQuillRef(el)}
                          theme="snow"
                          value={formData.html_body}
                          onChange={(content) => handleInputChange('html_body', content)}
                          modules={{
                            toolbar: [
                              [{ 'header': [1, 2, 3, false] }],
                              ['bold', 'italic', 'underline'],
                              [{ 'color': [] }, { 'background': [] }],
                              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                              ['link'],
                              [{ 'align': [] }],
                              ['clean']
                            ]
                          }}
                          formats={[
                            'header',
                            'bold', 'italic', 'underline',
                            'color', 'background',
                            'list', 'bullet',
                            'link',
                            'align'
                          ]}
                          placeholder="Enter your message here..."
                        />
                      </Box>
                    </>
                  )}
                  
                  <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                    Image (Optional)
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="simple-image-upload"
                    type="file"
                    onChange={(e) => handleImageUpload(e, (url) => setSimpleImage(url))}
                  />
                  <label htmlFor="simple-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={isUploading}
                      fullWidth
                      className={classes.formField}
                    >
                      {isUploading ? 'Uploading...' : simpleImage ? 'Change Image' : 'Upload Image'}
                    </Button>
                  </label>
                  
                  {simpleImage && (
                    <Box mb={2}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <img src={simpleImage} alt="Preview" className={classes.imagePreview} />
                        <Button size="small" color="secondary" onClick={() => setSimpleImage('')}>
                          Remove
                        </Button>
                      </Box>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>Image Position</InputLabel>
                        <Select
                          value={simpleImagePosition}
                          onChange={(e) => setSimpleImagePosition(e.target.value)}
                          label="Image Position"
                        >
                          <MenuItem value="top">Top (above content)</MenuItem>
                          <MenuItem value="bottom">Bottom (below content)</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>
              )}

              {templateType === 'newsletter' && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Newsletter Sections
                  </Typography>
                  {newsletterSections.map((section, index) => (
                    <Box key={section.id} className={classes.newsletterSection}>
                      <Box className={classes.sectionHeader}>
                        <Typography variant="body2" color="textSecondary">
                          Section {index + 1}
                        </Typography>
                        <Box className={classes.sectionControls}>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveSectionUp(index)}
                            disabled={index === 0}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveSectionDown(index)}
                            disabled={index === newsletterSections.length - 1}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveSection(section.id)}
                            disabled={newsletterSections.length === 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <TextField
                        label="Section Title"
                        fullWidth
                        value={section.title}
                        onChange={(e) => handleSectionChange(section.id, 'title', e.target.value)}
                        className={classes.formField}
                        variant="outlined"
                        size="small"
                      />

                      <Box className={classes.htmlModeToggle}>
                        <Button
                          size="small"
                          startIcon={<CodeIcon />}
                          onClick={() => setNewsletterHtmlMode(prev => ({
                            ...prev,
                            [section.id]: !prev[section.id]
                          }))}
                          variant={newsletterHtmlMode[section.id] ? 'contained' : 'outlined'}
                          color={newsletterHtmlMode[section.id] ? 'primary' : 'default'}
                        >
                          {newsletterHtmlMode[section.id] ? 'Visual Editor' : 'HTML Source'}
                        </Button>
                      </Box>

                      {newsletterHtmlMode[section.id] ? (
                        <TextField
                          label="Section Description (HTML)"
                          fullWidth
                          multiline
                          rows={4}
                          value={section.description}
                          onChange={(e) => handleSectionChange(section.id, 'description', e.target.value)}
                          className={classes.formField}
                          variant="outlined"
                          size="small"
                          InputProps={{
                            style: { fontFamily: 'monospace', fontSize: 13 }
                          }}
                        />
                      ) : (
                        <>
                          <MergeTagButtons 
                            quillRef={newsletterQuillRefs[section.id]} 
                            editorId={`newsletter-${section.id}`}
                            campaignType={formData.campaign_type}
                          />
                          <Box className={classes.richTextEditor} style={{ marginBottom: 16 }}>
                            <ReactQuill
                              ref={(el) => {
                                if (el && (!newsletterQuillRefs[section.id] || newsletterQuillRefs[section.id] !== el)) {
                                  setNewsletterQuillRefs(prev => ({ ...prev, [section.id]: el }));
                                }
                              }}
                              theme="snow"
                              value={section.description}
                              onChange={(content) => handleSectionChange(section.id, 'description', content)}
                              modules={{
                                toolbar: [
                                  [{ 'header': [1, 2, 3, false] }],
                                  ['bold', 'italic', 'underline'],
                                  [{ 'color': [] }, { 'background': [] }],
                                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                  ['link'],
                                  [{ 'align': [] }],
                                  ['clean']
                                ]
                              }}
                              formats={[
                                'header',
                                'bold', 'italic', 'underline',
                                'color', 'background',
                                'list', 'bullet',
                                'link',
                                'align'
                              ]}
                              placeholder="Enter section description..."
                            />
                          </Box>
                        </>
                      )}
                      
                      <Typography variant="caption" display="block" gutterBottom>
                        Section Image (Optional)
                      </Typography>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id={`newsletter-section-${section.id}-upload`}
                        type="file"
                        onChange={(e) => handleImageUpload(e, (url) => handleSectionChange(section.id, 'image_url', url))}
                      />
                      <label htmlFor={`newsletter-section-${section.id}-upload`}>
                        <Button
                          variant="outlined"
                          component="span"
                          size="small"
                          disabled={isUploading}
                          fullWidth
                          className={classes.formField}
                        >
                          {isUploading ? 'Uploading...' : section.image_url ? 'Change Image' : 'Upload Image'}
                        </Button>
                      </label>
                      
                      {section.image_url && (
                        <Box mb={2} display="flex" alignItems="center">
                          <img src={section.image_url} alt="Preview" className={classes.imagePreview} />
                          <Button size="small" color="secondary" onClick={() => handleSectionChange(section.id, 'image_url', '')}>
                            Remove
                          </Button>
                        </Box>
                      )}
                      
                      <FormControl fullWidth variant="outlined" size="small" className={classes.formField}>
                        <InputLabel>Image Position</InputLabel>
                        <Select
                          value={section.image_position}
                          onChange={(e) => handleSectionChange(section.id, 'image_position', e.target.value)}
                          label="Image Position"
                        >
                          <MenuItem value="top">Top (above text)</MenuItem>
                          <MenuItem value="bottom">Bottom (below text)</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddSection}
                    fullWidth
                  >
                    Add Section
                  </Button>
                </Box>
              )}

              {templateType === 'alert' && (
                <Box>
                  <FormControl fullWidth variant="outlined" className={classes.formField}>
                    <InputLabel>Alert Level</InputLabel>
                    <Select
                      value={alertLevel}
                      onChange={(e) => setAlertLevel(e.target.value)}
                      label="Alert Level"
                    >
                      {alertLevelOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <span>{option.icon}</span>
                            <span>{option.label}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box className={classes.htmlModeToggle}>
                    <Button
                      size="small"
                      startIcon={<CodeIcon />}
                      onClick={() => setIsHtmlMode(!isHtmlMode)}
                      variant={isHtmlMode ? 'contained' : 'outlined'}
                      color={isHtmlMode ? 'primary' : 'default'}
                    >
                      {isHtmlMode ? 'Visual Editor' : 'HTML Source'}
                    </Button>
                  </Box>

                  {isHtmlMode ? (
                    <TextField
                      label="HTML Source"
                      fullWidth
                      multiline
                      rows={8}
                      value={formData.html_body}
                      onChange={(e) => handleInputChange('html_body', e.target.value)}
                      className={classes.formField}
                      variant="outlined"
                      placeholder="<p>Enter HTML here...</p>"
                      InputProps={{
                        style: { fontFamily: 'monospace', fontSize: 13 }
                      }}
                    />
                  ) : (
                    <>
                      <MergeTagButtons quillRef={alertQuillRef} editorId="alert" campaignType={formData.campaign_type} />
                      <Box className={classes.richTextEditor}>
                        <ReactQuill
                          ref={(el) => setAlertQuillRef(el)}
                          theme="snow"
                          value={formData.html_body}
                          onChange={(content) => handleInputChange('html_body', content)}
                          modules={{
                            toolbar: [
                              [{ 'header': [1, 2, 3, false] }],
                              ['bold', 'italic', 'underline'],
                              [{ 'color': [] }, { 'background': [] }],
                              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                              ['link'],
                              [{ 'align': [] }],
                              ['clean']
                            ]
                          }}
                          formats={[
                            'header',
                            'bold', 'italic', 'underline',
                            'color', 'background',
                            'list', 'bullet',
                            'link',
                            'align'
                          ]}
                          placeholder="Enter your alert message..."
                        />
                      </Box>
                    </>
                  )}
                  
                  <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                    Alert Image (Optional)
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="alert-image-upload"
                    type="file"
                    onChange={(e) => handleImageUpload(e, (url) => setAlertImage(url))}
                  />
                  <label htmlFor="alert-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={isUploading}
                      fullWidth
                      className={classes.formField}
                    >
                      {isUploading ? 'Uploading...' : alertImage ? 'Change Image' : 'Upload Image'}
                    </Button>
                  </label>
                  
                  {alertImage && (
                    <Box mb={2}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <img src={alertImage} alt="Preview" className={classes.imagePreview} />
                        <Button size="small" color="secondary" onClick={() => setAlertImage('')}>
                          Remove
                        </Button>
                      </Box>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>Image Position</InputLabel>
                        <Select
                          value={simpleImagePosition}
                          onChange={(e) => setSimpleImagePosition(e.target.value)}
                          label="Image Position"
                        >
                          <MenuItem value="top">Top (above alert box)</MenuItem>
                          <MenuItem value="bottom">Bottom (below alert box)</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>
              )}

              {templateType === 'blank' && (
                <TextField
                  label="Email Content (HTML)"
                  fullWidth
                  multiline
                  rows={15}
                  value={formData.html_body}
                  onChange={(e) => handleInputChange('html_body', e.target.value)}
                  className={classes.formField}
                  variant="outlined"
                  helperText="No header or footer will be added. Use raw HTML."
                  placeholder="<h1>Your HTML here...</h1>"
                />
              )}

              <Divider style={{ margin: '24px 0' }} />

              {/* Call to Action */}
              <Box mb={3}>
                {formData.campaign_type !== 'survey' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={ctaEnabled}
                        onChange={(e) => setCtaEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={<Typography variant="subtitle1">Add Call to Action Button</Typography>}
                  />
                )}
                
                {formData.campaign_type === 'survey' && (
                  <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>
                    Survey Button
                  </Typography>
                )}
                
                {(ctaEnabled || formData.campaign_type === 'survey') && (
                  <Box mt={2}>
                    <TextField
                      label="Message (optional)"
                      fullWidth
                      multiline
                      rows={2}
                      value={ctaMessage}
                      onChange={(e) => setCtaMessage(e.target.value)}
                      className={classes.formField}
                      variant="outlined"
                      placeholder="Text to display above the button"
                    />
                    <TextField
                      label="Button Text"
                      fullWidth
                      value={ctaButtonText}
                      onChange={(e) => setCtaButtonText(e.target.value)}
                      className={classes.formField}
                      variant="outlined"
                      placeholder="e.g., Learn More, Contact Us, Get Started"
                    />
                    <TextField
                      label={formData.campaign_type === 'survey' ? 'Button URL (Auto-generated)' : 'Button URL'}
                      fullWidth
                      value={ctaButtonUrl}
                      onChange={(e) => setCtaButtonUrl(e.target.value)}
                      className={classes.formField}
                      variant="outlined"
                      placeholder="https://example.com"
                      helperText={formData.campaign_type === 'survey' ? 'Survey link is automatically generated for each recipient' : 'URL will be automatically tracked for clicks'}
                      disabled={formData.campaign_type === 'survey'}
                      InputProps={{
                        readOnly: formData.campaign_type === 'survey',
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Divider style={{ margin: '16px 0' }} />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeUnsubscribe}
                    onChange={(e) => setIncludeUnsubscribe(e.target.checked)}
                    color="primary"
                    disabled={templateType === 'blank'}
                  />
                }
                label="Include unsubscribe link in footer"
              />

                </>
              )}

              <Divider style={{ margin: '16px 0' }} />

              {/* Scheduling Section */}
              <Typography variant="h6" gutterBottom style={{ marginTop: 16 }}>
                Schedule Campaign
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={enableScheduling}
                    onChange={(e) => {
                      setEnableScheduling(e.target.checked);
                      if (!e.target.checked) {
                        setScheduledSendTime('');
                      }
                    }}
                    color="primary"
                  />
                }
                label="Schedule for later (instead of sending immediately)"
              />

              {enableScheduling && (
                <Box mt={2}>
                  {/* Info Box */}
                  <Box 
                    mb={2} 
                    p={2} 
                    style={{ 
                      backgroundColor: '#e3f2fd', 
                      border: '1px solid #2196f3',
                      borderRadius: '4px' 
                    }}
                  >
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <AccessTimeIcon style={{ fontSize: '1.2rem', color: '#1565c0' }} />
                      <Typography variant="body2" style={{ color: '#1565c0', fontWeight: 500 }}>
                        Your Current Timezone: <strong>{browserTimezone}</strong>
                      </Typography>
                    </Box>
                    <Typography variant="caption" style={{ color: '#1976d2' }}>
                      The system detected your timezone automatically. You can change it below if needed.
                    </Typography>
                  </Box>

                  {/* Timezone Selector */}
                  <FormControl fullWidth variant="outlined" className={classes.formField}>
                    <InputLabel>Timezone for Scheduling</InputLabel>
                    <Select
                      value={selectedTimezone}
                      onChange={(e) => setSelectedTimezone(e.target.value)}
                      label="Timezone for Scheduling"
                    >
                      <MenuItem value="America/New_York">Eastern Time (US & Canada)</MenuItem>
                      <MenuItem value="America/Chicago">Central Time (US & Canada)</MenuItem>
                      <MenuItem value="America/Denver">Mountain Time (US)</MenuItem>
                      <MenuItem value="America/Los_Angeles">Pacific Time (US & Canada)</MenuItem>
                      <MenuItem value="Europe/London">London (GMT/BST)</MenuItem>
                      <MenuItem value="Europe/Paris">Paris (CET/CEST)</MenuItem>
                      <MenuItem value="Asia/Dubai">Dubai (GST)</MenuItem>
                      <MenuItem value="Asia/Kuwait">Kuwait (AST)</MenuItem>
                      <MenuItem value="Asia/Riyadh">Riyadh (AST)</MenuItem>
                      <MenuItem value="Asia/Tokyo">Tokyo (JST)</MenuItem>
                      <MenuItem value="Asia/Shanghai">Shanghai (CST)</MenuItem>
                      <MenuItem value="Australia/Sydney">Sydney (AEST/AEDT)</MenuItem>
                      <MenuItem value="UTC">UTC (Universal Time)</MenuItem>
                    </Select>
                  </FormControl>

                  {/* DateTime Picker */}
                  <TextField
                    label={`Scheduled Send Time in ${selectedTimezone}`}
                    type="datetime-local"
                    fullWidth
                    value={scheduledSendTime}
                    onChange={(e) => setScheduledSendTime(e.target.value)}
                    className={classes.formField}
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText={
                      <span>
                        <EventIcon style={{ fontSize: '0.875rem', verticalAlign: 'middle', marginRight: '4px' }} />
                        Enter the time in <strong>{selectedTimezone}</strong> timezone.
                        <br />
                        The campaign will be sent at this exact time in {selectedTimezone.split('/')[1] || 'UTC'}.
                        <br />
                        The system stores times in UTC internally and will send at the correct moment.
                      </span>
                    }
                  />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Right: Preview */}
          <Grid item xs={12} md={6} className={classes.previewSection}>
            <Paper className={classes.previewPaper}>
              <Typography variant="h6" gutterBottom>
                Email Preview
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Subject:</strong> {formData.subject || '(No subject)'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>From:</strong> {formData.from_name || '(No name)'} &lt;{formData.from_email || '(No email)'}&gt;
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>To:</strong> {getRecipientCount()} recipient(s)
                </Typography>
              </Box>
              <div 
                className={classes.previewContent}
                dangerouslySetInnerHTML={{ 
                  __html: buildEmailHTML() || '<p style="color: #999; text-align: center; padding: 40px;">Email preview will appear here as you type...</p>' 
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      </div>

      {/* Fixed Action Bar */}
      <div className={classes.actionBar}>
        <Button
          variant="outlined"
          onClick={() => history.push('/email-campaigns')}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="default"
          startIcon={isSendingTest ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleTestSend}
          disabled={isSaving || isSending || isSendingTest}
        >
          {isSendingTest ? 'Sending...' : 'Send Test to Me'}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSaveDraft}
          disabled={isSaving || isSending || isSendingTest}
        >
          Save Draft
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={isSending ? <CircularProgress size={20} /> : (enableScheduling ? <ScheduleIcon /> : <SendIcon />)}
          onClick={handleSendNow}
          disabled={isSaving || isSending || isSendingTest}
        >
          {enableScheduling ? 'Schedule Campaign' : 'Send Now'}
        </Button>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        confirmText="Send Campaign"
        confirmColor="primary"
      />
    </div>
  );
};

export default CreateEmailCampaign;
